import { useEffect, useState, useContext, useRef } from "react";
import notificationSound from "../Sound/3beeps-108353.mp3";
import muteIcon from "../images/enable-sound.png";
import { GlobalContext } from "../Context/GlobalContext";
import useOrders from "../services/Order/useOrder";

const OrderWebSocket = ({ onNewOrder }) => {
  const audioRef = useRef(null);
  const socketRef = useRef(null);
  const [isSoundPlaying, setIsSoundPlaying] = useState(false);
  const [currentCatalogueStatus, setCurrentCatalogueStatus] = useState("");
  const [showSoundReminder, setShowSoundReminder] = useState(false);
  const reminderTimeoutRef = useRef(null);
  const {
    baseUrl,
    basicAuth,
    userID,
    addUnreadOrder,
    isMuted,
    setWsConnected,
  } = useContext(GlobalContext);
  const [processedOrders, setProcessedOrders] = useState(() => {
    const savedOrders = localStorage.getItem("processedOrders");
    return savedOrders ? JSON.parse(savedOrders) : [];
  });

  const config = {
    headers: {
      Authorization: basicAuth,
      "Content-Type": "application/json",
    },
  };

  const { getCatalogueStatus } = useOrders();

  // Initialize audio once
  useEffect(() => {
    const audio = new Audio(notificationSound);
    audio.preload = "auto";
    audio.loop = true;
    audio.volume = 1.0;
    audioRef.current = audio;

    const unlockAudio = () => {
      audio.muted = true;
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.muted = false;
          localStorage.setItem("soundUnlocked", "true");
          console.log(" Audio unlocked");
        })
        .catch((err) => {
          console.warn("Unlock failed:", err);
        });

      document.removeEventListener("click", unlockAudio);
      document.removeEventListener("mousemove", unlockAudio);
    };

    if (localStorage.getItem("soundUnlocked") !== "true") {
      document.addEventListener("click", unlockAudio, { once: true });
      document.addEventListener("mousemove", unlockAudio, { once: true });
    }

    return () => {
      document.removeEventListener("click", unlockAudio);
      document.removeEventListener("mousemove", unlockAudio);
    };
  }, []);

  const stopNotificationSound = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    setIsSoundPlaying(false);
  };

  const isMutedRef = useRef(isMuted);

  useEffect(() => {
    const handleAnyClick = () => {
      if (audioRef.current && !audioRef.current.paused) {
        stopNotificationSound();
      }
    };

    document.addEventListener("click", handleAnyClick);

    return () => {
      document.removeEventListener("click", handleAnyClick);
    };
  }, []);

  useEffect(() => {
    if (!userID) {
      console.error("userID is missing. WebSocket connection not started.");
      return;
    }

    let uniqueId = sessionStorage.getItem("uniqueId");
    if (!uniqueId) {
      uniqueId = crypto.randomUUID();
      sessionStorage.setItem("uniqueId", uniqueId);
    }

    let interval;

    const connectWebSocket = () => {
      if (socketRef.current) {
        socketRef.current.onclose = null;
        socketRef.current.close();
      }

      console.log(" Connecting to WebSocket with userID:", userID);

      const socket = new WebSocket(
        `wss://api.anlook.com/ws?userId=${uniqueId}&roomId=${userID}_order_room_v1`,
      );

      socketRef.current = socket;

      socket.onopen = () => {
        console.log(" WebSocket Connected");
        setWsConnected(true);
      };

      socket.onmessage = (event) => {
        console.log(" WebSocket Message Received:", event.data);

        try {
          const orderData = JSON.parse(event.data);
          console.log(" Parsed Order Data:", orderData);

          if (orderData.Message === "New Order Received") {

            if (audioRef.current && !isMutedRef.current) {
              if (!audioRef.current.paused) {
                audioRef.current.pause();
              }

              audioRef.current.currentTime = 0;

              audioRef.current
                .play()
                .then(() => {
                  setIsSoundPlaying(true);
                  console.log(" Sound ON - playing");
                })
                .catch((err) => console.warn("Sound play failed:", err));
            } else {
              console.log(" Sound OFF - muted");
            }

            addUnreadOrder({ ...orderData, _uid: crypto.randomUUID() });
            onNewOrder();
            // getNewOrder();
          }
        } catch (error) {
          console.error(
            " Error parsing WebSocket message:",
            error,
            "Data:",
            event.data,
          );
        }
      };

      socket.onerror = (error) => {
        console.error(" WebSocket Error:", error);
        setWsConnected(false);
      };

      socket.onclose = (event) => {
        console.warn(" WebSocket Closed:", event.reason, "Code:", event.code);
        setWsConnected(false);
      };
    };

    // Initial connect
    connectWebSocket();

    // Reconnect every 1 minute
    interval = setInterval(
      () => {
        connectWebSocket();
      },
      1 * 60 * 1000,
    );

    return () => {
      clearInterval(interval);

      if (socketRef.current) {
        socketRef.current.onclose = null;
        socketRef.current.close();
      }

      stopNotificationSound();
    };
  }, [userID]);

  // useEffect(() => {
  //   if (!userID) return;

  //   const initCatalogueStatus = async () => {
  //     const res = await getCatalogueStatus();

  //     if (res?.responseCode === "00") {
  //       setCurrentCatalogueStatus(res.response?.catalogueStatus || "");
  //     }
  //   };

  //   initCatalogueStatus();
  // }, [userID]);

  useEffect(() => {
  if (!userID) {
    setCurrentCatalogueStatus("");
    return;
  }

  let isMounted = true;

  const initCatalogueStatus = async () => {
    const res = await getCatalogueStatus();

    if (!isMounted) return;

    if (res?.responseCode === "00") {
      const status =
        res?.response?.catalogueStatus ??
        sessionStorage.getItem(`catalogue_status_${userID}`) ??
        "";

      setCurrentCatalogueStatus(status);
    }
  };

  initCatalogueStatus();

  return () => {
    isMounted = false;
  };
}, [userID, getCatalogueStatus]);

  useEffect(() => {
    const checkReminder = async () => {
      const audio = audioRef.current;
      if (!audio) return;

      try {
        await audio.play();
        audio.pause();
        localStorage.setItem("soundUnlocked", "true");
      } catch (err) {
        localStorage.removeItem("soundUnlocked");

        if (currentCatalogueStatus === "show") {
          reminderTimeoutRef.current = setTimeout(() => {
            setShowSoundReminder(true);
          }, 500);
        }
      }
    };

    checkReminder();

    return () => {
      clearTimeout(reminderTimeoutRef.current);
    };
  }, [currentCatalogueStatus]);

  useEffect(() => {
    isMutedRef.current = isMuted;

    if (isMuted) {
      stopNotificationSound();
    }
  }, [isMuted]);

  return (
    <>
      {showSoundReminder && (
        <div
          onClick={() => {
            setShowSoundReminder(false);
            const unlockAudio = () => {
              if (audioRef.current) {
                audioRef.current.muted = true;
                audioRef.current
                  .play()
                  .then(() => {
                    audioRef.current.pause();
                    audioRef.current.muted = false;
                    localStorage.setItem("soundUnlocked", "true");
                  })
                  .catch((err) => console.warn("Manual unlock failed:", err));
              }
            };
            unlockAudio();
          }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "pointer",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              padding: "16px 40px",
              maxWidth: "600px",
              width: "90%",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              textAlign: "center",
              animation: "fadeInUp 0.6s ease-out",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <img
                src={muteIcon}
                style={{ height: "100px", display: "block", margin: "0 auto" }}
                alt=""
              />
              <h2
                style={{
                  fontSize: "1.7rem",
                  margin: "8px 0",
                  color: "#222",
                  fontWeight: "600",
                }}
              >
                Sound Alerts : OFF
              </h2>
              <p style={{ fontSize: "1.4rem", color: "#555", margin: "6px 0" }}>
                Click anywhere on the screen to enable sound notifications.
              </p>
            </div>
          </div>
          <style>{`
            @keyframes fadeInUp {
              from { transform: translateY(30px); opacity: 0; }
              to { transform: translateY(0); opacity: 1; }
            }
          `}</style>
        </div>
      )}
    </>
  );
};

export default OrderWebSocket;
